import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, IconButton, Menu, MenuItem, Popover, Typography } from '@mui/material';
import {
  ArrowCheckIcon,
  CancelIcon,
  PhoneIcon,
  SettingIcon,
  SyncIcon,
  WarningIcon,
} from '@sharedCR/coreDesignSystem/icons';
import ErrorBoundary from 'app/ErrorBoundary';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { IconDirection, IconSize } from 'constants/coreDesignSystem';
import { CallStatus, RenderingContainerType, UserStatusLabels } from 'constants/telephony';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import usePopover from 'hooks/usePopover';
import { getIsNativePlatform } from 'utils/platform';

import { getMasterRole } from 'store/auth/selectors';
import {
  fetchTelephonyUserAction,
  setSelectedInputDeviceAction,
  setSelectedOutputDeviceAction,
  setTelephonyActiveTabAction,
  setTelephonyWidgetOpenAction,
  updateUserStatusAction,
} from 'store/system/actions';
import {
  selectCallStatus,
  selectConnectionHealthStatus,
  selectHasTelnyxEverConnected,
  selectIsLoanOfficer,
  selectIsSignalRReconnecting,
  selectIsTelephonyUserLoaded,
  selectIsTelnyxConnected,
  selectIsTelnyxConnecting,
  selectTelephonyActiveTab,
  selectUserContactStatus,
} from 'store/system/selectors';

import ActiveCall from './components/ActiveCall';
import AudioDeviceSettings from './components/AudioDeviceSettings';
import { getPersistedAudioSettings } from './components/AudioDeviceSettings/audioDeviceSettings.utils';
import BorrowerChat from './components/BorrowerChat';
import CallHistory from './components/CallHistory';
import Contacts from './components/Contacts';
import Dialer from './components/Dialer';
import IncomingCall from './components/IncomingCall';
import InternalChat from './components/InternalChat';
import LeadNotification from './components/LeadNotification';
import OutgoingCall from './components/OutgoingCall';
import PostCallForm from './components/PostCallForm';
import TelephonySignalRContainer from './components/TelephonySignalRContainer';
import TelephonyTabBar from './components/TelephonyTabBar';
import TextMessaging from './components/TextMessaging';
import Voicemails from './components/Voicemails';
import { ACTIVE_TAB_STORAGE_KEY, PLACEHOLDER_ICON_SIZE } from './TelephonyWidget.constants';
import {
  availabilityDotStyles,
  closeButtonStyles,
  connectionBannerErrorStyles,
  connectionBannerWarningStyles,
  contentAreaStyles,
  contentWrapperStyles,
  headerActionsStyles,
  headerLeftStyles,
  headerStyles,
  phoneButtonStyles,
  placeholderIconStyles,
  placeholderTextStyles,
  popoverPaperStyles,
  reconnectingLabelStyles,
  settingsButtonStyles,
  statusDotStyles,
  statusMenuDotStyles,
  statusMenuItemStyles,
  statusRowStyles,
} from './TelephonyWidget.styles';
import { TelephonyWidgetTestIds } from './TelephonyWidget.testIds';
import { getStatusColor, getStatusOptions, getVisibleTabs } from './TelephonyWidget.utils';

const POPOVER_ID = 'telephony-popover';
const STATUS_MENU_ID = 'telephony-status-menu';

const popoverOrigin = {
  anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
  transformOrigin: { vertical: 'top', horizontal: 'center' },
};

const slotProps = { paper: { sx: popoverPaperStyles } };

const StatusMenuItem = memo(({ option, isSelected, onSelect }) => {
  const dotStyles = useMemo(
    () => ({ ...statusMenuDotStyles, backgroundColor: option.color }),
    [option.color],
  );

  const handleClick = useCallback(() => {
    onSelect(option.value);
  }, [onSelect, option.value]);

  return (
    <MenuItem
      data-testid={TelephonyWidgetTestIds.STATUS_OPTION}
      selected={isSelected}
      sx={statusMenuItemStyles}
      onClick={handleClick}
    >
      <Box sx={dotStyles} />
      <Typography variant="regular14">{option.label}</Typography>
    </MenuItem>
  );
});

StatusMenuItem.displayName = 'StatusMenuItem';

StatusMenuItem.propTypes = {
  isSelected: PropTypes.bool.isRequired,
  option: PropTypes.shape({
    color: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  }).isRequired,
  onSelect: PropTypes.func.isRequired,
};

const TelephonyWidget = () => {
  const dispatch = useDispatch();
  const activeTab = useSelector(selectTelephonyActiveTab);
  const contactStatus = useSelector(selectUserContactStatus);
  const isLoanOfficer = useSelector(selectIsLoanOfficer);
  const masterRoles = useSelector(getMasterRole);
  const callStatus = useSelector(selectCallStatus);
  const isSignalRReconnecting = useSelector(selectIsSignalRReconnecting);
  const isTelnyxConnected = useSelector(selectIsTelnyxConnected);
  const isTelnyxConnecting = useSelector(selectIsTelnyxConnecting);
  const connectionHealth = useSelector(selectConnectionHealthStatus);
  const isUserLoaded = useSelector(selectIsTelephonyUserLoaded);
  const { anchorEl, onSetAnchorElement, onClearAnchorElement } = usePopover();
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const phoneButtonRef = useRef(null);

  const hasTelnyxEverConnected = useSelector(selectHasTelnyxEverConnected);

  // Only show "reconnecting" state if we have connected at least once before.
  // On initial mount, !isConnected && !isConnecting is true but that's not a reconnection.
  const isTelnyxReconnecting = hasTelnyxEverConnected && !isTelnyxConnected && !isTelnyxConnecting;

  const visibleTabs = useMemo(() => getVisibleTabs(masterRoles), [masterRoles]);

  const activeTabConfig = useMemo(
    () => visibleTabs.find(tab => tab.key === activeTab),
    [visibleTabs, activeTab],
  );
  const PlaceholderIcon = activeTabConfig?.Icon ?? PhoneIcon;

  const statusOptions = useMemo(() => getStatusOptions(isLoanOfficer), [isLoanOfficer]);
  const statusColor = useMemo(() => getStatusColor(contactStatus), [contactStatus]);

  const iconDotStyles = useMemo(
    () => ({ ...availabilityDotStyles, backgroundColor: statusColor }),
    [statusColor],
  );

  const headerDotStyles = useMemo(
    () => ({ ...statusDotStyles, backgroundColor: statusColor }),
    [statusColor],
  );

  const isInCall = callStatus !== CallStatus.IDLE;

  // DEV-4332: on native, CallKit is the SOLE incoming/ringing answer surface. Suppress the in-app
  // incoming presentation here (no auto-open, no answer/decline UI) so there is exactly one answer
  // path (CallKit -> native plugin) and the two surfaces don't race the SIP answer. OUTGOING /
  // ACTIVE / POST_CALL in-call presentation is untouched on native.
  const isNativeIncomingSuppressed = getIsNativePlatform() && callStatus === CallStatus.INCOMING;

  useEffectSafeStrictMode(() => {
    if (!isUserLoaded) {
      dispatch(fetchTelephonyUserAction());
    }

    // Restore persisted audio device selections into telnyxService on mount.
    // This ensures the selected mic/speaker are available for calls even if
    // the user never opens Audio Device Settings after a page load.
    try {
      const persisted = getPersistedAudioSettings();

      if (persisted.selectedInputDeviceId) {
        dispatch(setSelectedInputDeviceAction(persisted.selectedInputDeviceId));
      }

      if (persisted.selectedOutputDeviceId) {
        dispatch(setSelectedOutputDeviceAction(persisted.selectedOutputDeviceId));
      }
    } catch {
      // localStorage unavailable — use system defaults
    }
  }, []);

  // Restore the persisted active tab once visibleTabs includes role-restricted entries.
  // This runs in a separate effect so it re-fires when masterRoles loads and visibleTabs
  // updates, but the ref guard ensures it only restores the tab once.
  const hasRestoredTabRef = useRef(false);

  useEffect(() => {
    if (hasRestoredTabRef.current) {
      return;
    }

    try {
      const savedTab = localStorage.getItem(ACTIVE_TAB_STORAGE_KEY);
      const isValidTab =
        savedTab &&
        Object.values(RenderingContainerType).includes(savedTab) &&
        visibleTabs.some(tab => tab.key === savedTab);

      if (isValidTab) {
        hasRestoredTabRef.current = true;
        dispatch(setTelephonyActiveTabAction(savedTab));
      }
    } catch {
      // localStorage unavailable — use default tab
    }
  }, [visibleTabs, dispatch]);

  // Auto-open the widget when an incoming call arrives so the user can see and answer it.
  // Skipped on native (isNativeIncomingSuppressed): CallKit owns the incoming surface there.
  useEffect(() => {
    if (
      callStatus === CallStatus.INCOMING &&
      !isNativeIncomingSuppressed &&
      !anchorEl &&
      phoneButtonRef.current
    ) {
      onSetAnchorElement({ currentTarget: phoneButtonRef.current, stopPropagation: _.noop });
      dispatch(setTelephonyWidgetOpenAction(true));
    }
  }, [callStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const content = useMemo(() => {
    switch (callStatus) {
      case CallStatus.INCOMING:
        // CallKit owns the incoming/answer surface on native — render no in-app answer/decline UI.
        return isNativeIncomingSuppressed ? null : <IncomingCall />;
      case CallStatus.OUTGOING:
        return <OutgoingCall />;
      case CallStatus.ACTIVE:
        return <ActiveCall />;
      case CallStatus.POST_CALL:
        return <PostCallForm />;
      default:
        break;
    }

    if (activeTab === RenderingContainerType.KEYPAD) {
      return <Dialer />;
    }

    if (activeTab === RenderingContainerType.CONTACTS) {
      return <Contacts />;
    }

    if (activeTab === RenderingContainerType.HISTORY) {
      return <CallHistory />;
    }

    if (activeTab === RenderingContainerType.TEXT_SMS) {
      return <TextMessaging />;
    }

    if (activeTab === RenderingContainerType.CHAT) {
      return <BorrowerChat />;
    }

    if (activeTab === RenderingContainerType.INTERNAL_CHAT) {
      return <InternalChat />;
    }

    if (activeTab === RenderingContainerType.VOICEMAILS) {
      return <Voicemails />;
    }

    return (
      <Box sx={contentAreaStyles}>
        <Box sx={placeholderIconStyles}>
          <PlaceholderIcon size={PLACEHOLDER_ICON_SIZE} />
        </Box>
        <Typography sx={placeholderTextStyles} variant="regular16">
          {activeTabConfig?.label ?? 'Telephony'}
        </Typography>
      </Box>
    );
  }, [callStatus, activeTab, activeTabConfig, isNativeIncomingSuppressed]);

  const onOpen = useCallback(
    e => {
      onSetAnchorElement(e);
      dispatch(setTelephonyWidgetOpenAction(true));
    },
    [dispatch, onSetAnchorElement],
  );

  const onClose = useCallback(
    e => {
      onClearAnchorElement(e);
      dispatch(setTelephonyWidgetOpenAction(false));
    },
    [dispatch, onClearAnchorElement],
  );

  const onOpenStatusMenu = useCallback(e => {
    setStatusMenuAnchor(e.currentTarget);
  }, []);

  const onStatusRowKeyDown = useCallback(e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setStatusMenuAnchor(e.currentTarget);
    }
  }, []);

  const onCloseStatusMenu = useCallback(() => {
    setStatusMenuAnchor(null);
  }, []);

  const onSelectStatus = useCallback(
    status => {
      dispatch(updateUserStatusAction({ contactStatus: status }));
      setStatusMenuAnchor(null);
    },
    [dispatch],
  );

  const onOpenSettings = useCallback(() => {
    setShowSettings(true);
  }, []);

  const onCloseSettings = useCallback(() => {
    setShowSettings(false);
  }, []);

  return (
    <ErrorBoundary>
      <TelephonySignalRContainer />
      <LeadNotification />

      <IconButton
        aria-controls={anchorEl ? POPOVER_ID : undefined}
        aria-expanded={!!anchorEl}
        aria-haspopup="dialog"
        aria-label="Telephony"
        data-is-active={!!anchorEl}
        data-is-incoming={callStatus === CallStatus.INCOMING}
        data-is-reconnecting={isTelnyxReconnecting}
        data-testid={TelephonyWidgetTestIds.WIDGET}
        ref={phoneButtonRef}
        sx={phoneButtonStyles}
        onClick={onOpen}
      >
        <PhoneIcon color="inherit" size={IconSize[24]} />
        <Box sx={iconDotStyles} />
      </IconButton>

      <Popover
        disableEnforceFocus
        anchorEl={anchorEl}
        anchorOrigin={popoverOrigin.anchorOrigin}
        id={POPOVER_ID}
        open={!!anchorEl}
        slotProps={slotProps}
        transformOrigin={popoverOrigin.transformOrigin}
        onClose={onClose}
      >
        <Box sx={headerStyles}>
          <Box sx={headerLeftStyles}>
            <Typography variant="bold20">Telephony</Typography>
            {isSignalRReconnecting && (
              <Typography sx={reconnectingLabelStyles} variant="regular12">
                Reconnecting…
              </Typography>
            )}

            <Box
              aria-controls={statusMenuAnchor ? STATUS_MENU_ID : undefined}
              aria-expanded={!!statusMenuAnchor}
              aria-haspopup="menu"
              role="button"
              sx={statusRowStyles}
              tabIndex={0}
              onClick={onOpenStatusMenu}
              onKeyDown={onStatusRowKeyDown}
            >
              <Box sx={headerDotStyles} />
              <Typography variant="regular12">
                {UserStatusLabels[contactStatus] ?? contactStatus}
              </Typography>
              <ArrowCheckIcon direction={IconDirection.DOWN} size={IconSize[16]} />
            </Box>

            <Menu
              anchorEl={statusMenuAnchor}
              id={STATUS_MENU_ID}
              open={!!statusMenuAnchor}
              onClose={onCloseStatusMenu}
            >
              {statusOptions.map(option => (
                <StatusMenuItem
                  isSelected={option.value === contactStatus}
                  key={option.value}
                  option={option}
                  onSelect={onSelectStatus}
                />
              ))}
            </Menu>
          </Box>

          <Box sx={headerActionsStyles}>
            <IconButton
              disableRipple
              data-testid={TelephonyWidgetTestIds.SETTINGS}
              sx={settingsButtonStyles}
              onClick={onOpenSettings}
            >
              <SettingIcon size={IconSize[20]} />
            </IconButton>
            <IconButton
              disableRipple
              data-testid={TelephonyWidgetTestIds.CLOSE}
              sx={closeButtonStyles}
              onClick={onClose}
            >
              <CancelIcon size={IconSize[20]} />
            </IconButton>
          </Box>
        </Box>

        {connectionHealth.message && (
          <Box
            sx={
              connectionHealth.hasError
                ? connectionBannerErrorStyles
                : connectionBannerWarningStyles
            }
          >
            {connectionHealth.hasError ? (
              <WarningIcon size={IconSize[16]} />
            ) : (
              <SyncIcon size={IconSize[16]} />
            )}
            <Typography variant="regular12">{connectionHealth.message}</Typography>
          </Box>
        )}

        <Box sx={contentWrapperStyles}>
          {showSettings ? <AudioDeviceSettings onBack={onCloseSettings} /> : content}
        </Box>

        {!isInCall && !showSettings && <TelephonyTabBar tabs={visibleTabs} />}
      </Popover>
    </ErrorBoundary>
  );
};

export default memo(TelephonyWidget);
